import React, { useState, useEffect, useCallback } from "react";
import { apiRequests } from "../../shared/api/api";
import {
  Tabs,
  Button,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Card,
  Statistic,
  Row,
  Col,
  Space,
  Badge,
  Tooltip,
  Popconfirm,
  message,
  Typography,
  Divider,
  Switch,
  Spin,
  Empty,
  Alert,
  Radio,
} from "antd";
import {
  PlusOutlined,
  QrcodeOutlined,
  BarChartOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const C_BTN = {
  Max: "#1677ff",
  Telegram: "#0088cc",
  SMS: "#52c41a",
  Other: "#fa8c16",
};
const C_CHART = [
  "#1677ff",
  "#52c41a",
  "#faad14",
  "#f5222d",
  "#722ed1",
  "#13c2c2",
];

const QR_SRC = (code, size = 100) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    `${window.location.origin}/qr/${code}`,
  )}`;

const SHORT_URL = (code) => `qrrun.ru/${code}`;

function ButtonQRBlock({ buttons = [] }) {
  if (!buttons.length) return null;
  return (
    <Space wrap size={12}>
      {buttons.map((btn) => (
        <Card
          key={btn.type}
          size="small"
          bodyStyle={{ padding: 10, textAlign: "center" }}
          style={{ width: 120 }}
        >
          <img
            src={QR_SRC(btn.short_code, 80)}
            alt={btn.type}
            style={{ width: 80, height: 80 }}
          />
          <div style={{ marginTop: 4 }}>
            <Tag color={C_BTN[btn.type] || "default"} style={{ fontSize: 11 }}>
              {btn.type}
            </Tag>
          </div>
          <Text
            copyable={{
              text: `${window.location.origin}/qr/${btn.short_code}`,
            }}
            style={{ fontSize: 10, color: "#1677ff" }}
          >
            {SHORT_URL(btn.short_code)}
          </Text>
        </Card>
      ))}
    </Space>
  );
}

function PreviewModal({ open, onClose, page }) {
  if (!page) return null;
  const isSVG = (s) => typeof s === "string" && s.trim().startsWith("<svg");

  const buttons = page.buttons || [];
  const landing = `${window.location.origin}/qr/${page.short_code}`;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Превью мини-лендинга"
      footer={null}
      width={480}
    >
      <div style={{ textAlign: "center", padding: "0 8px" }}>
        {page.logo &&
          (isSVG(page.logo) ? (
            <div
              dangerouslySetInnerHTML={{ __html: page.logo }}
              style={{ height: 60, marginBottom: 8 }}
            />
          ) : (
            <div style={{ fontSize: 40, marginBottom: 8 }}>{page.logo}</div>
          ))}
        {page.image_url &&
          (isSVG(page.image_url) ? (
            <div
              dangerouslySetInnerHTML={{ __html: page.image_url }}
              style={{ marginBottom: 12, maxHeight: 140, overflow: "hidden" }}
            />
          ) : (
            <img
              src={page.image_url}
              alt="cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
              style={{
                width: "100%",
                maxHeight: 140,
                objectFit: "cover",
                borderRadius: 10,
                marginBottom: 12,
              }}
            />
          ))}

        <Title level={4} style={{ marginBottom: 4 }}>
          {page.name}
        </Title>
        {page.description && (
          <Paragraph type="secondary" style={{ fontSize: 13 }}>
            {page.description}
          </Paragraph>
        )}

        <Space
          wrap
          style={{ justifyContent: "center", marginTop: 16, marginBottom: 16 }}
        >
          {buttons.map((btn) => (
            <Button
              key={btn.type}
              type="primary"
              style={{
                background: C_BTN[btn.type] || "#1677ff",
                borderColor: "transparent",
              }}
            >
              {btn.label}
            </Button>
          ))}
        </Space>

        <Divider style={{ margin: "12px 0" }} />
        <ButtonQRBlock buttons={buttons} />

        <Divider style={{ margin: "12px 0" }} />
        <Space direction="vertical" size={2} style={{ alignItems: "center" }}>
          <img
            src={QR_SRC(page.short_code, 120)}
            alt="QR"
            style={{ width: 120, height: 120 }}
          />
          <Text copyable={{ text: landing }} style={{ fontSize: 12 }}>
            {landing}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            UTM: {page.utm_source} / {page.utm_medium} / {page.utm_campaign}
          </Text>
          {page.auto_tags?.length > 0 && (
            <Space size={2} wrap style={{ justifyContent: "center" }}>
              {page.auto_tags.map((t) => (
                <Tag key={t} style={{ fontSize: 10 }}>
                  {t}
                </Tag>
              ))}
            </Space>
          )}
        </Space>
      </div>
    </Modal>
  );
}

function VisitsModal({ open, onClose, page, visits, loading }) {
  const cols = [
    {
      title: "Дата/время",
      dataIndex: "created_at",
      key: "ts",
      width: 155,
      render: (v) => (v ? new Date(v).toLocaleString("ru-RU") : "—"),
    },
    { title: "IP", dataIndex: "ip", key: "ip", width: 130 },
    {
      title: "Кнопка",
      dataIndex: "button_type",
      key: "btn",
      width: 110,
      render: (v) =>
        v ? (
          <Tag color={C_BTN[v] || "default"}>{v}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Цель",
      dataIndex: "target_reached",
      key: "tr",
      width: 140,
      render: (v) =>
        v ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Регистрация
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />}>Нет</Tag>
        ),
    },
    {
      title: "Yandex CID",
      dataIndex: "yandex_cid",
      key: "ycid",
      width: 120,
      render: (v) => v || "—",
    },
    {
      title: "Google CID",
      dataIndex: "google_cid",
      key: "gcid",
      width: 120,
      render: (v) => v || "—",
    },
    {
      title: "Реферер",
      dataIndex: "referer",
      key: "ref",
      ellipsis: true,
      render: (v) =>
        v ? (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {v}
          </Text>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={960}
      footer={null}
      title={<>📋 {page?.name} — история переходов</>}
    >
      <Table
        dataSource={visits}
        columns={cols}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20, showTotal: (t) => `Всего: ${t}` }}
        locale={{ emptyText: "Нет переходов по QR-коду" }}
      />
    </Modal>
  );
}

function DetailCharts({ stats }) {
  if (!stats) return <Spin style={{ display: "block", margin: "32px auto" }} />;
  const { daily = [], buttons = [] } = stats;

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} lg={15}>
        <Card title="Динамика переходов и регистраций" size="small">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={daily} margin={{ left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RTooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                dataKey="visits"
                fill="#1677ff"
                name="Переходы"
                radius={[3, 3, 0, 0]}
              />
              <Bar
                dataKey="registrations"
                fill="#52c41a"
                name="Регистрации"
                radius={[3, 3, 0, 0]}
              />
              <Bar
                dataKey="clicks"
                fill="#fa8c16"
                name="Клики"
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Col>
      <Col xs={24} lg={9}>
        <Card title="Конверсия CR%" size="small">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={daily} margin={{ left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <RTooltip formatter={(v) => `${v}%`} />
              <Line
                type="monotone"
                dataKey="conversion"
                stroke="#722ed1"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="CR%"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </Col>
      {buttons.length > 0 && (
        <Col xs={24}>
          <Card title="Распределение по кнопкам" size="small">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={buttons}
                  dataKey="count"
                  nameKey="button_type"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ button_type, count }) => `${button_type}: ${count}`}
                >
                  {buttons.map((b, i) => (
                    <Cell
                      key={b.button_type}
                      fill={C_BTN[b.button_type] || C_CHART[i % 6]}
                    />
                  ))}
                </Pie>
                <RTooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      )}
    </Row>
  );
}

function CampaignFormModal({ open, onClose, onSave, initial }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [collectUtm, setCollectUtm] = useState(true);

  useEffect(() => {
    if (open) {
      const v = initial || {};
      form.setFieldsValue({
        source_name: v.source_name || "",
        name: v.name || "",
        description: v.description || "",
        image_url: v.image_url || "",
        logo: v.logo || "",
        utm_source: v.utm_source || "qr_cashier",
        utm_medium: v.utm_medium || "qr_physical",
        utm_campaign: v.utm_campaign || "loyalty_card",
        collect_form_utm: v.collect_form_utm !== false,
        auto_tags: (v.auto_tags || []).join(", "),
        status: v.status || "active",
      });
      setCollectUtm(v.collect_form_utm !== false);
    }
  }, [open, initial, form]);

  const handleFinish = async (vals) => {
    setSaving(true);
    try {
      await onSave({
        ...vals,
        auto_tags: vals.auto_tags
          ? vals.auto_tags
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean)
          : [],
        buttons: [
          { type: "Max", label: "Через Max" },
          { type: "Telegram", label: "Telegram" },
          { type: "SMS", label: "Через SMS" },
        ],
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={600}
      title={initial ? "Редактировать кампанию" : "Новая кампания"}
      onOk={() => form.submit()}
      okText="Сохранить"
      confirmLoading={saving}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="source_name"
          label="Назовите данный источник"
          tooltip="Например: «Касса №3», «Витрина входная», «Листовка у стойки»"
        >
          <Input placeholder="Касса №1 — основная" />
        </Form.Item>
        <Form.Item
          name="name"
          label="Название кампании"
          rules={[{ required: true, message: "Укажите название" }]}
        >
          <Input placeholder="Premium Кешбэк 2025" />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <TextArea
            rows={2}
            placeholder="Сканируй QR на кассе → получи баллы и выбери удобный канал"
          />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="image_url"
              label="Изображение (URL или SVG)"
              tooltip="Можно вставить URL картинки или inline SVG-код"
            >
              <TextArea
                rows={3}
                placeholder={"https://... или\n<svg xmlns=...>...</svg>"}
                style={{ fontSize: 12 }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="logo"
              label="Логотип (эмодзи, текст или SVG)"
              tooltip="Эмодзи, текст, или inline SVG"
            >
              <TextArea
                rows={3}
                placeholder={"💎 PremiumClub\n\nили <svg>...</svg>"}
                style={{ fontSize: 12 }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="utm_source" label="utm_source">
              <Input placeholder="qr_cashier" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="utm_medium" label="utm_medium">
              <Input placeholder="qr_physical" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="utm_campaign" label="utm_campaign">
              <Input placeholder="loyalty_card" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="collect_form_utm" valuePropName="checked" label={null}>
          <Switch
            checked={collectUtm}
            onChange={(v) => {
              setCollectUtm(v);
              form.setFieldValue("collect_form_utm", v);
            }}
          />
          <Text style={{ marginLeft: 8, fontSize: 13 }}>
            Собирать обычные UTM из адресной строки при переходе?
          </Text>
        </Form.Item>

        <Form.Item
          name="auto_tags"
          label="Автоматические теги (через запятую)"
          tooltip="Эти теги будут автоматически проставлены на каждый переход"
        >
          <Input placeholder="offline, cashier, loyalty, summer2025" />
        </Form.Item>

        {/* === Статус === */}
        <Form.Item name="status" label="Статус">
          <Radio.Group>
            <Radio.Button value="active">Активна</Radio.Button>
            <Radio.Button value="draft">Черновик</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Alert
          type="info"
          showIcon
          message={
            <span style={{ fontSize: 12 }}>
              После создания под каждую кнопку (Max, Telegram, SMS) будет
              сгенерирован отдельный QR с уникальным коротким URL. Так вы будете
              знать, через какой канал пришёл каждый клиент.
            </span>
          }
        />
      </Form>
    </Modal>
  );
}

function CampaignsTable({
  pages,
  loading,
  onEdit,
  onDelete,
  onPreview,
  onLog,
  onStats,
}) {
  const cols = [
    {
      title: "QR / Источник",
      key: "qr",
      width: 160,
      render: (_, r) => (
        <Space direction="vertical" size={0} align="center">
          <Tooltip title="Нажмите для превью">
            <img
              src={QR_SRC(r.short_code, 56)}
              alt="QR"
              style={{ width: 56, height: 56, cursor: "pointer" }}
              onClick={() => onPreview(r)}
            />
          </Tooltip>
          <Text style={{ fontSize: 10, color: "#1677ff" }}>
            <Text
              copyable={{
                text: `${window.location.origin}/qr/${r.short_code}`,
              }}
              style={{ fontSize: 10, color: "#1677ff" }}
            >
              {SHORT_URL(r.short_code)}
            </Text>
          </Text>
          {r.source_name && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              {r.source_name}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Кампания",
      key: "info",
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Text strong>{r.name}</Text>
          {r.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.description}
            </Text>
          )}
          {r.auto_tags?.length > 0 && (
            <Space size={2} wrap>
              {r.auto_tags.map((t) => (
                <Tag key={t} style={{ fontSize: 10, margin: "1px 2px" }}>
                  {t}
                </Tag>
              ))}
            </Space>
          )}
          <Text type="secondary" style={{ fontSize: 11 }}>
            utm: {r.utm_source}/{r.utm_medium}/{r.utm_campaign}
          </Text>
        </Space>
      ),
    },
    {
      title: "Кнопки-ссылки",
      key: "btns",
      width: 220,
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          {(r.buttons || []).map((btn) => (
            <Space key={btn.type} size={4}>
              <Tag
                color={C_BTN[btn.type] || "default"}
                style={{ fontSize: 11, minWidth: 64 }}
              >
                {btn.type}
              </Tag>
              <Text
                copyable={{
                  text: `${window.location.origin}/qr/${btn.short_code}`,
                }}
                style={{ fontSize: 11, color: "#1677ff" }}
              >
                {SHORT_URL(btn.short_code)}
              </Text>
            </Space>
          ))}
        </Space>
      ),
    },
    {
      title: "Переходов",
      key: "v",
      width: 90,
      render: (_, r) => (
        <Text strong style={{ color: "#1677ff" }}>
          {r.stats?.visits ?? 0}
        </Text>
      ),
    },
    {
      title: "Кликов",
      key: "cl",
      width: 80,
      render: (_, r) => (
        <Text style={{ color: "#fa8c16" }}>{r.stats?.clicks ?? 0}</Text>
      ),
    },
    {
      title: "Регистраций",
      key: "rg",
      width: 110,
      render: (_, r) => (
        <Text strong style={{ color: "#52c41a" }}>
          {r.stats?.registrations ?? 0}
        </Text>
      ),
    },
    {
      title: "CR%",
      key: "cr",
      width: 70,
      render: (_, r) => (
        <Text style={{ color: "#722ed1" }}>{r.stats?.conversion ?? 0}%</Text>
      ),
    },
    {
      title: "Дата создания",
      dataIndex: "created_at",
      key: "ca",
      width: 110,
      render: (v) => (v ? new Date(v).toLocaleDateString("ru-RU") : "—"),
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "st",
      width: 95,
      render: (v) => (
        <Badge
          status={v === "active" ? "success" : "default"}
          text={v === "active" ? "Активна" : "Черновик"}
        />
      ),
    },
    {
      title: "Действия",
      key: "act",
      width: 160,
      render: (_, r) => (
        <Space wrap size={4}>
          <Tooltip title="Превью">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => onPreview(r)}
            />
          </Tooltip>
          <Tooltip title="Аналитика">
            <Button
              icon={<BarChartOutlined />}
              size="small"
              onClick={() => onStats(r)}
            />
          </Tooltip>
          <Tooltip title="Визиты">
            <Button
              icon={<TeamOutlined />}
              size="small"
              onClick={() => onLog(r)}
            />
          </Tooltip>
          <Tooltip title="Редактировать">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(r)}
            />
          </Tooltip>
          <Popconfirm
            title="Удалить кампанию?"
            onConfirm={() => onDelete(r.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Tooltip title="Удалить">
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      dataSource={pages}
      columns={cols}
      rowKey="id"
      loading={loading}
      size="small"
      scroll={{ x: 1200 }}
      pagination={{ pageSize: 15, showTotal: (t) => `Всего: ${t}` }}
      locale={{
        emptyText: <Empty description="Нет кампаний. Создайте первую." />,
      }}
    />
  );
}

function AnalyticsTab({ pages, selId, onSel, stats, statsLoading }) {
  const options = pages.map((p) => ({
    value: p.id,
    label: `${p.source_name ? p.source_name + " — " : ""}${p.name}`,
  }));

  const barData = pages.map((p) => ({
    name: (p.source_name || p.name).slice(0, 16),
    Переходы: p.stats?.visits || 0,
    Регистрации: p.stats?.registrations || 0,
    Клики: p.stats?.clicks || 0,
  }));

  return (
    <Row gutter={[12, 12]}>
      {/* Сравнение всех */}
      <Col xs={24} xl={14}>
        <Card title="Сравнение кампаний" size="small">
          {barData.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={barData} margin={{ left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <RTooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Переходы" fill="#1677ff" radius={[3, 3, 0, 0]} />
                <Bar
                  dataKey="Регистрации"
                  fill="#52c41a"
                  radius={[3, 3, 0, 0]}
                />
                <Bar dataKey="Клики" fill="#fa8c16" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="Нет данных" />
          )}
        </Card>
      </Col>

      {/* Выбор страницы + summary */}
      <Col xs={24} xl={10}>
        <Card title="Детальная аналитика" size="small">
          <Select
            placeholder="Выберите кампанию"
            style={{ width: "100%", marginBottom: 16 }}
            options={options}
            value={selId}
            onChange={onSel}
            allowClear
            showSearch
            filterOption={(inp, opt) =>
              opt.label.toLowerCase().includes(inp.toLowerCase())
            }
          />
          {selId && stats?.summary && !statsLoading && (
            <Row gutter={12}>
              {[
                { t: "Переходы", v: stats.summary.visits, c: "#1677ff" },
                {
                  t: "Регистрации",
                  v: stats.summary.registrations,
                  c: "#52c41a",
                },
                { t: "Клики", v: stats.summary.clicks, c: "#fa8c16" },
                { t: "CR%", v: `${stats.summary.conversion}%`, c: "#722ed1" },
              ].map((s) => (
                <Col span={12} key={s.t} style={{ marginBottom: 12 }}>
                  <Statistic
                    title={<span style={{ fontSize: 11 }}>{s.t}</span>}
                    value={s.v}
                    valueStyle={{ color: s.c, fontSize: 18 }}
                  />
                </Col>
              ))}
            </Row>
          )}
          {statsLoading && (
            <Spin style={{ display: "block", margin: "16px auto" }} />
          )}
        </Card>
      </Col>

      {/* Detail charts */}
      {selId && stats && !statsLoading && (
        <Col xs={24}>
          <DetailCharts stats={stats} />
        </Col>
      )}
    </Row>
  );
}

export default function LoyaltyQRPage({ token, websocket, initialData }) {
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("campaigns");

  const [formOpen, setFormOpen] = useState(false);
  const [formInit, setFormInit] = useState(null);

  const [previewPage, setPreviewPage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [visitsPage, setVisitsPage] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitsOpen, setVisitsOpen] = useState(false);
  const [visitsLoading, setVisitsLoading] = useState(false);

  const [selPageId, setSelPageId] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    apiRequests.setToken(token);
  }, [token]);

  const reload = useCallback(async () => {
    setPagesLoading(true);
    try {
      const response = await apiRequests.loyaltyQr.getPages();
      const data = response.data;
      setPages(Array.isArray(data) ? data : []);
    } catch {
      message.error("Ошибка загрузки кампаний");
    } finally {
      setPagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) reload();
  }, [token, reload]);

  const handleSave = useCallback(
    async (vals) => {
      if (formInit?.id) {
        await apiRequests.loyaltyQr.updatePage(formInit.id, vals);
        message.success("Обновлено");
      } else {
        await apiRequests.loyaltyQr.createPage(vals);
        message.success("Кампания создана");
      }
      setFormOpen(false);
      setFormInit(null);
      reload();
    },
    [formInit, reload],
  );

  const handleDelete = useCallback(
    async (id) => {
      await apiRequests.loyaltyQr.deletePage(id);
      message.success("Удалено");
      reload();
    },
    [reload],
  );

  const handleLog = useCallback(async (page) => {
    setVisitsPage(page);
    setVisitsOpen(true);
    setVisitsLoading(true);
    try {
      const response = await apiRequests.loyaltyQr.getVisits(page.id);
      const data = response.data;
      setVisits(Array.isArray(data) ? data : []);
    } catch {
      message.error("Ошибка загрузки визитов");
    } finally {
      setVisitsLoading(false);
    }
  }, []);

  const handleSelPage = useCallback(async (id) => {
    setSelPageId(id);
    if (!id) {
      setStats(null);
      return;
    }
    setStatsLoading(true);
    try {
      const response = await apiRequests.loyaltyQr.getStats(id, 7);
      const data = response.data;
      setStats(data);
    } catch {
      message.error("Ошибка загрузки статистики");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const handleStatsBtn = useCallback(
    (page) => {
      setActiveTab("analytics");
      handleSelPage(page.id);
    },
    [handleSelPage],
  );

  return (
    <div style={{ padding: "0 8px" }}>
      {/* Header */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16, marginTop: 8 }}
      >
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={reload}
              loading={pagesLoading}
            >
              Обновить
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setFormInit(null);
                setFormOpen(true);
              }}
            >
              Новая кампания
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "campaigns",
            label: (
              <Space>
                <QrcodeOutlined />
                Кампании
              </Space>
            ),
            children: (
              <CampaignsTable
                pages={pages}
                loading={pagesLoading}
                onEdit={(r) => {
                  setFormInit(r);
                  setFormOpen(true);
                }}
                onDelete={handleDelete}
                onPreview={(r) => {
                  setPreviewPage(r);
                  setPreviewOpen(true);
                }}
                onLog={handleLog}
                onStats={handleStatsBtn}
              />
            ),
          },
          {
            key: "analytics",
            label: (
              <Space>
                <BarChartOutlined />
                Аналитика
              </Space>
            ),
            children: (
              <AnalyticsTab
                pages={pages}
                selId={selPageId}
                onSel={handleSelPage}
                stats={stats}
                statsLoading={statsLoading}
              />
            ),
          },
        ]}
      />

      {/* Modals */}
      <CampaignFormModal
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setFormInit(null);
        }}
        onSave={handleSave}
        initial={formInit}
      />

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        page={previewPage}
      />

      <VisitsModal
        open={visitsOpen}
        onClose={() => setVisitsOpen(false)}
        page={visitsPage}
        visits={visits}
        loading={visitsLoading}
      />
    </div>
  );
}
